import { useEffect } from 'react';
import { useStudio } from './state/store.ts';
import { Topbar } from './components/Topbar.tsx';
import { togglePlay, toggleRecord } from './components/Transport.tsx';
import { Toast, toast } from './components/ui.tsx';
import { engine } from './audio/engine.ts';
import './audio/bridgefx.ts';   // eigene Plugins über die Bridge (rechnen, live, Original-Fenster)
import { api } from './api/client.ts';
import { updatePresence } from './collab/session.ts';
import { Session } from './views/Session.tsx';
import { Compose } from './views/Compose.tsx';
import { Mix } from './views/Mix.tsx';
import { Klang } from './views/Klang.tsx';
import { Splits } from './views/Splits.tsx';
import { Camp, Login } from './views/Camp.tsx';
import { SrsMark } from './components/Icons.tsx';
import { setSrsBasis } from './srs/srs.ts';
import { Ar, Katalog } from './views/Ar.tsx';
import { Dante } from './views/Dante.tsx';
import { MixerOverlay } from './views/Mixer.tsx';
import { useEdit } from './state/edit.ts';
import { handleEditKey } from './edit/commands.ts';
import { pianoFocus } from './views/PianoRoll.tsx';

export function App() {
  const view = useStudio((s) => s.view);
  const token = useStudio((s) => s.token);

  // Gemerkte Anmeldung beim Start prüfen (abgelaufen = neu anmelden)
  useEffect(() => { if (useStudio.getState().token) api.me().catch(() => undefined); }, []);

  useEffect(() => {
    const ping = () => api.health().then((h) => { if (h.login?.srsApi) setSrsBasis(h.login.srsApi); useStudio.getState().setOnline({ server: true }); }).catch(() => useStudio.getState().setOnline({ server: false }));
    void ping();
    const t = setInterval(ping, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { updatePresence({ view }); }, [view]);

  // Tastenkürzel wie in Pro Tools / Logic
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select')) return;
      const st = useStudio.getState(), mod = e.metaKey || e.ctrlKey;
      if (e.code === 'Space') { e.preventDefault(); void togglePlay(); return; }
      if (e.key === 'x' && !mod && !e.altKey) { st.setMixerOpen(!st.mixerOpen); if (!st.mixerOpen) void engine.ensure(); return; }
      if (e.key === 'Escape' && st.mixerOpen && !st.pluginWin) { st.setMixerOpen(false); return; }
      if (e.key === 'Escape' && st.view === 'session') { useEdit.getState().setRange(null); st.select([]); return; }
      // Loop-Aufnahme an/aus (Pro Tools: Option+L)
      if (e.altKey && !mod && e.code === 'KeyL') { e.preventDefault(); st.setTransport({ loopRecord: !st.transport.loopRecord }); toast(st.transport.loopRecord ? 'Loop-Aufnahme aus' : 'Loop-Aufnahme an: jeder Durchgang wird ein Take'); return; }
      // Edit-Befehle wie in Pro Tools (nur im Edit-Fenster, nicht über dem Mixer)
      if (st.view === 'session' && !st.mixerOpen && !pianoFocus.active && handleEditKey(e)) return;
      if (mod && e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) st.redo(); else st.undo(); return; }
      if (mod || e.altKey) return;
      if (e.key === 'r') void toggleRecord();
      else if (e.key === 'Enter') engine.locate(0);
      else if (e.key === 'k') st.setTransport({ metronome: !st.transport.metronome });
      else if (e.key === 'l') {
        const r = useEdit.getState().range;
        st.commit('Loop', (p) => { p.loop.enabled = !p.loop.enabled; if (p.loop.enabled && r && r.end - r.start > 0.01) { p.loop.start = r.start; p.loop.end = r.end; } });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!token) return (
    <div className="gate">
      <div className="gate-brand"><SrsMark size={44} /><div><div className="wm" style={{ color: 'var(--ink)', fontSize: 22 }}>SMART ROYALTY</div><div className="mono t2" style={{ letterSpacing: '.2em', fontSize: 11, marginTop: 4 }}>STUDIO</div></div></div>
      <Login />
      <Toast />
    </div>
  );

  return (
    <div className="app">
      <Topbar />
      <main className="main">
        {view === 'camp' && <Camp />}
        {view === 'session' && <Session />}
        {view === 'compose' && <Compose />}
        {view === 'klang' && <Klang />}
        {view === 'mix' && <Mix />}
        {view === 'splits' && <Splits />}
        {view === 'ar' && <Ar />}
        {view === 'katalog' && <Katalog />}
        {view === 'dante' && <Dante />}
        <MixerOverlay />
      </main>
      <Toast />
    </div>
  );
}
